import React, { useState, useMemo, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  FormControl,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import toaster from 'react-hot-toast';
import AutocompleteDropdown from 'components/common/autocompleteDropdown/AutocompleteDropdown';
import { BusinessPicker } from 'components/common/businessPicker/BusinessPicker';
import { EnvironmentalVariables } from 'components/common/environmentalVariables/EnvironmentalVariables';
import { ReleaseVersionSelect } from 'components/common/firmware/ReleaseVersionSelect';
import { THEME_MESSAGES } from 'utils/constants';
import type { ReleaseVersion } from 'api/admin/firmware/firmware';
import { updateWashboardDevices } from 'api/admin/washboard/washboard';
import type { WashboardDevice } from 'api/admin/washboard/washboard';
import { useDeviceEnvVariables } from 'api/queries/useDeviceEnvVariables';
import { useStoresForBusiness } from 'api/queries/useStoresForBusiness';
import './BulkDeviceConfiguration.scss';
import { ConfirmChangesModal } from './components/ConfirmChangesModal';
import { getEnvVarDiff, toEnvVarChangeLists } from './utils/envVarDiff';

type Option = { value: string | number; display: string };

interface BulkDeviceConfigurationProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDevices: WashboardDevice[];
  onSaveSuccess?: () => void;
}

export const BulkDeviceConfiguration: React.FC<BulkDeviceConfigurationProps> = ({
  isOpen,
  onClose,
  selectedDevices,
  onSaveSuccess,
}) => {
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [initialStoreId, setInitialStoreId] = useState<number | null>(null);
  const [selectedRelease, setSelectedRelease] = useState<ReleaseVersion | null>(null);
  const [envVariables, setEnvVariables] = useState<Record<string, string>>({});
  const [hasEnvValidationErrors, setHasEnvValidationErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const deviceIds = useMemo(() => selectedDevices.map((d) => d.id), [selectedDevices]);
  const {
    data: envVarsData,
    isLoading: isEnvVarsLoading,
    error: envVarsError,
  } = useDeviceEnvVariables(deviceIds, isOpen);
  const devicesEnvVars = envVarsData?.devices ?? [];

  const { data: stores = [] } = useStoresForBusiness(
    selectedBusinessId ?? 0,
    Boolean(selectedBusinessId),
  );

  const storeOptions = useMemo(
    () =>
      (stores ?? []).map((store) => ({
        value: store.id,
        display: `${store.name} [ID: ${store.id}]`,
      })),
    [stores],
  );

  const selectedStoreOption = useMemo(() => {
    if (!selectedStoreId) return undefined;
    return storeOptions.find((option) => option.value === selectedStoreId);
  }, [selectedStoreId, storeOptions]);

  const hasPairedDevices = useMemo(() => {
    return selectedDevices.some((device) => device.machineName);
  }, [selectedDevices]);

  const sharedDeviceType = useMemo(() => {
    if (!selectedDevices.length) return null;

    const firstDeviceType = selectedDevices[0].deviceType;
    const allSameDeviceType = selectedDevices.every(
      (device) => device.deviceType === firstDeviceType,
    );

    return allSameDeviceType ? firstDeviceType : null;
  }, [selectedDevices]);

  const sharedRelease = useMemo<ReleaseVersion | null>(() => {
    const { releaseId, releaseVersion } = selectedDevices[0] ?? {};
    if (!releaseId) return null;

    const allSameRelease = selectedDevices.every((device) => device.releaseId === releaseId);

    return allSameRelease ? { id: releaseId, version: releaseVersion ?? '' } : null;
  }, [selectedDevices]);

  const isBusinessDisabled = hasPairedDevices;
  const isLocationDisabled = !selectedBusinessId || hasPairedDevices;

  const envAggregation = useMemo(() => {
    if (!devicesEnvVars.length) {
      return {
        totalDevices: 0,
        keysMismatch: false,
        mismatchedValuesCount: 0,
        initialEnvVariables: {},
        keyTooltips: {},
      };
    }

    const deviceIdToName = new Map(selectedDevices.map((d) => [d.id, d.name]));
    const deviceIdToUpdatedAt = new Map(devicesEnvVars.map((d) => [d.deviceId, d.updatedAt || '']));

    // Get all keys and find common keys
    const allKeys = new Set(devicesEnvVars.flatMap((d) => Object.keys(d.envVariables || {})));
    let commonKeys = new Set(allKeys);
    for (const device of devicesEnvVars) {
      const deviceKeys = new Set(Object.keys(device.envVariables || {}));
      commonKeys = new Set([...commonKeys].filter((k) => deviceKeys.has(k)));
    }

    const keysMismatch = commonKeys.size !== allKeys.size;
    const initialEnvVariables: Record<string, string> = {};
    const keyTooltips: Record<string, React.ReactNode> = {};
    let mismatchedValuesCount = 0;

    // Process each common key
    for (const key of [...commonKeys].sort()) {
      const valueToCountAndRecency = new Map<string, { count: number; latestUpdatedAt: string }>();
      const deviceRows: Array<{ deviceName: string; value: string }> = [];

      // Collect values per device
      for (const device of devicesEnvVars) {
        const value = device.envVariables?.[key] ?? '';
        const deviceName = deviceIdToName.get(device.deviceId) ?? '';
        const updatedAt = deviceIdToUpdatedAt.get(device.deviceId) ?? '';

        const existing = valueToCountAndRecency.get(value) || { count: 0, latestUpdatedAt: '' };
        existing.count += 1;
        if (updatedAt > existing.latestUpdatedAt) existing.latestUpdatedAt = updatedAt;
        valueToCountAndRecency.set(value, existing);

        deviceRows.push({ deviceName, value: value || '(unset)' });
      }

      const hasMultipleValues = valueToCountAndRecency.size > 1;
      if (hasMultipleValues) mismatchedValuesCount += 1;

      // Pick default: most common value, if same count pick by latest updatedAt
      let chosenValue = '';
      let highestCount = -1;
      let mostRecentUpdatedAt = '';

      for (const [value, { count, latestUpdatedAt }] of valueToCountAndRecency) {
        if (
          count > highestCount ||
          (count === highestCount && latestUpdatedAt > mostRecentUpdatedAt)
        ) {
          highestCount = count;
          mostRecentUpdatedAt = latestUpdatedAt;
          chosenValue = value;
        }
      }

      if (chosenValue) initialEnvVariables[key] = chosenValue;

      if (hasMultipleValues && chosenValue) {
        keyTooltips[key] = (
          <Box className="bulk-envvars-tooltip">
            {deviceRows.map((row) => (
              <Typography key={row.deviceName} variant="body2" className="bulk-envvars-tooltipRow">
                <span className="bulk-envvars-tooltipDevice">{row.deviceName}</span>: {row.value}
              </Typography>
            ))}
          </Box>
        );
      }
    }

    return {
      totalDevices: devicesEnvVars.length,
      keysMismatch,
      mismatchedValuesCount,
      initialEnvVariables,
      keyTooltips,
    };
  }, [devicesEnvVars, selectedDevices]);

  useEffect(() => {
    if (isOpen && selectedDevices.length > 0) {
      const firstBusinessId = selectedDevices[0].businessId;
      const allSameBusiness = selectedDevices.every((d) => d.businessId === firstBusinessId);

      const firstStoreId = selectedDevices[0].storeId;
      const allSameStore = selectedDevices.every((d) => d.storeId === firstStoreId);

      setSelectedBusinessId(allSameBusiness ? firstBusinessId : null);
      const storeIdToSet = allSameBusiness && allSameStore ? firstStoreId : null;
      setInitialStoreId(storeIdToSet);
      setSelectedStoreId(storeIdToSet);

      setSelectedRelease(sharedRelease);
      setEnvVariables({});
      setHasEnvValidationErrors(false);
    }
  }, [isOpen, selectedDevices, sharedRelease]);

  useEffect(() => {
    if (!isOpen) return;
    if (!devicesEnvVars.length) return;

    setEnvVariables(envAggregation.initialEnvVariables);
    setHasEnvValidationErrors(false);
  }, [isOpen, devicesEnvVars, envAggregation.initialEnvVariables]);

  const hasLocationChanged = useMemo(() => {
    if (!selectedStoreId) return false;
    return selectedStoreId !== initialStoreId;
  }, [selectedStoreId, initialStoreId]);

  const hasReleaseChanged = useMemo(() => {
    if (!sharedDeviceType || !selectedRelease) return false;
    return selectedRelease.id !== sharedRelease?.id;
  }, [sharedDeviceType, selectedRelease, sharedRelease]);

  const envVarDiff = useMemo(
    () => getEnvVarDiff(envAggregation.initialEnvVariables, envVariables),
    [envAggregation.initialEnvVariables, envVariables],
  );
  const hasEnvVarsChanged = envVarDiff.hasChanges;

  const changesList = useMemo(() => {
    const changes: {
      location?: string;
      releaseVersion?: string;
      addedEnvVars?: string[];
      modifiedEnvVars?: string[];
      deletedEnvVars?: string[];
    } = {};

    if (hasLocationChanged && selectedStoreId) {
      changes.location = selectedStoreOption?.display || `ID: ${selectedStoreId}`;
    }

    if (hasReleaseChanged && selectedRelease) {
      changes.releaseVersion = selectedRelease.version || `ID: ${selectedRelease.id}`;
    }

    if (hasEnvVarsChanged) {
      const envVarChanges = toEnvVarChangeLists(envVariables, envVarDiff);
      changes.addedEnvVars = envVarChanges.addedEnvVars;
      changes.modifiedEnvVars = envVarChanges.modifiedEnvVars;
      changes.deletedEnvVars = envVarChanges.deletedEnvVars;
    }

    return changes;
  }, [
    envVariables,
    envVarDiff,
    hasEnvVarsChanged,
    hasLocationChanged,
    hasReleaseChanged,
    selectedRelease,
    selectedStoreId,
    selectedStoreOption?.display,
  ]);

  const handleStoreChange = (option: Option | null) => {
    const value = option?.value ?? null;
    setSelectedStoreId(value as number | null);
  };

  const handleEnvVariablesChange = (
    envVars: Record<string, string>,
    hasValidationErrors: boolean,
    _hasChanges: boolean,
  ) => {
    setEnvVariables(envVars);
    setHasEnvValidationErrors(hasValidationErrors);
  };

  const handleSave = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    try {
      setIsSaving(true);
      const deviceIds = selectedDevices.map((device) => device.id);

      await updateWashboardDevices({
        deviceIds,
        locationId: hasLocationChanged ? selectedStoreId ?? undefined : undefined,
        envVariables: hasEnvVarsChanged ? envVariables : undefined,
        releaseId: hasReleaseChanged ? selectedRelease?.id : undefined,
      });

      toaster.success('Update process was started for selected devices.');
      setShowConfirmModal(false);
      onSaveSuccess?.();
      onClose();
    } catch (error) {
      toaster.error((error as any)?.response?.data?.error || THEME_MESSAGES.somethingWrong);
    } finally {
      setIsSaving(false);
    }
  };

  const isSaveDisabled =
    isSaving ||
    (!hasLocationChanged && !hasEnvVarsChanged && !hasReleaseChanged) ||
    hasEnvValidationErrors;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      classes={{
        paper: 'device-config-panel',
      }}
    >
      <div className="device-config-panel-container">
        <div className="device-config-panel-header">
          <Typography variant="h5" className="device-config-panel-title">
            Edit Device Configuration
          </Typography>
          <IconButton onClick={onClose} className="device-config-panel-close">
            <CloseIcon />
          </IconButton>
        </div>
        <div className="device-config-panel-content">
          <Box className="device-config-section">
            {!isBusinessDisabled && (
              <FormControl fullWidth className="config-form-control">
                <BusinessPicker
                  id="device-config-business"
                  selectedBusinessId={selectedBusinessId}
                  onChange={(id) => {
                    setSelectedBusinessId(id);
                    setSelectedStoreId(null);
                  }}
                  disabled={isBusinessDisabled}
                />
              </FormControl>
            )}
            {!isBusinessDisabled && (
              <FormControl fullWidth className="config-form-control">
                <AutocompleteDropdown
                  id="device-config-location"
                  selectedData={selectedStoreOption}
                  onChange={handleStoreChange}
                  label="Location"
                  selectData={storeOptions}
                  size="medium"
                  disabled={isLocationDisabled}
                />
              </FormControl>
            )}
            {sharedDeviceType && (
              <FormControl fullWidth className="config-form-control">
                <ReleaseVersionSelect
                  id="device-config-release"
                  deviceType={sharedDeviceType}
                  selectedRelease={selectedRelease}
                  onChange={setSelectedRelease}
                  size="medium"
                />
              </FormControl>
            )}
          </Box>
          <Box className="device-config-section">
            <Typography variant="h6" className="section-title">
              Environmental Variables
            </Typography>
            {isEnvVarsLoading ? (
              <Box className="bulk-envvars-loading">
                <CircularProgress size={18} />
              </Box>
            ) : envVarsError ? (
              <Alert severity="warning" className="bulk-envvars-alert">
                {(envVarsError as any)?.response?.data?.error || 'Failed to load env variables'}
              </Alert>
            ) : envAggregation.totalDevices > 0 ? (
              <>
                {envAggregation.keysMismatch && (
                  <Alert severity="info" className="bulk-envvars-alert">
                    Selected devices do not have the same set of environment variable keys. Only
                    keys present on all selected devices are shown.
                  </Alert>
                )}
                {envAggregation.mismatchedValuesCount > 0 && (
                  <Alert severity="info" className="bulk-envvars-alert">
                    Some environment variable values differ across devices. Hover the tooltip icon
                    next to a key to see the values for each device.
                  </Alert>
                )}
              </>
            ) : null}

            <EnvironmentalVariables
              envVariables={envVariables}
              onChange={handleEnvVariablesChange}
              keyTooltips={envAggregation.keyTooltips}
            />
          </Box>
        </div>
        <div className="device-config-panel-footer">
          <Button variant="outlined" color="primary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            Save Changes
          </Button>
        </div>
      </div>

      <ConfirmChangesModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
        isBulk
        deviceCount={selectedDevices.length}
        changes={changesList}
        isLoading={isSaving}
      />
    </Drawer>
  );
};
